import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke, toolSuccess, toolError } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import {
  appendConverterBatchUsage,
  analyticsParams,
  canvasQuality,
  converterBatchUsage,
  drawForOutput,
  DIRECTION_CONFIG,
  isConverterZipWorkloadAllowed,
  MAX_CONVERTER_BATCH_FILES,
  MAX_CONVERTER_BATCH_INPUT_BYTES,
  MAX_CONVERTER_FILE_SIZE,
  outputFilename,
  validateConverterFile,
  type ConversionDirection,
  type ConverterLandingSource,
  type ImageFormat,
  type OutputFormat,
} from '@/pages/png-jpg-converter/model/converter'
import { ToolHero } from '@/shared/ui/ToolHero'
type ConverterErrors = Translations['converter']['errorsText']

interface FileEntry {
  id: string
  file: File
  originalUrl: string
  originalSize: number
  convertedBlob: Blob | null
  convertedSize: number | null
  status: 'pending' | 'converting' | 'done' | 'error'
  error?: string
  detectedFormat: ImageFormat
  width: number
  height: number
}

/** Unique id for each file entry. */
let nextId = 0
function uid(): string {
  return `img-${++nextId}-${Date.now()}`
}

/** Human-readable file size. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

/** Convert an image via canvas from one format to another. */
function convertImage(
  img: HTMLImageElement,
  format: OutputFormat,
  quality: number,
  errors: ConverterErrors,
  signal: AbortSignal,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    let settled = false

    const release = () => {
      signal.removeEventListener('abort', handleAbort)
      canvas.width = 0
      canvas.height = 0
    }
    const finish = (callback: () => void) => {
      if (settled) return
      settled = true
      release()
      callback()
    }
    const handleAbort = () => finish(() => reject(new DOMException('Aborted', 'AbortError')))

    if (signal.aborted) {
      handleAbort()
      return
    }
    signal.addEventListener('abort', handleAbort, { once: true })

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      finish(() => reject(new Error(errors.canvas)))
      return
    }

    try {
      drawForOutput(ctx, img, canvas.width, canvas.height, format)
    } catch {
      finish(() => reject(new Error(errors.conversion)))
      return
    }

    canvas.toBlob(
      (blob) => {
        if (signal.aborted) {
          handleAbort()
        } else if (blob?.type === format) {
          finish(() => resolve(blob))
        } else {
          finish(() => reject(new Error(errors.conversion)))
        }
      },
      format,
      format === 'image/jpeg' ? canvasQuality(quality) : undefined,
    )
  })
}

/** Convert a single file entry. */
async function convertEntry(
  entry: FileEntry,
  format: OutputFormat,
  quality: number,
  errors: ConverterErrors,
  signal: AbortSignal,
): Promise<Blob> {
  const img = await loadImage(entry.originalUrl, errors, signal)
  try {
    return await convertImage(img, format, quality, errors, signal)
  } finally {
    img.onload = null
    img.onerror = null
    img.src = ''
  }
}

/** Load an image from a src URL. */
function loadImage(
  src: string,
  errors: ConverterErrors,
  signal: AbortSignal,
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    let settled = false
    const finish = (callback: () => void) => {
      if (settled) return
      settled = true
      signal.removeEventListener('abort', handleAbort)
      callback()
    }
    const handleAbort = () => {
      img.onload = null
      img.onerror = null
      img.src = ''
      finish(() => reject(new DOMException('Aborted', 'AbortError')))
    }
    if (signal.aborted) {
      handleAbort()
      return
    }
    signal.addEventListener('abort', handleAbort, { once: true })
    img.onload = () => finish(() => resolve(img))
    img.onerror = () => finish(() => reject(new Error(errors.imageLoad)))
    img.src = src
  })
}

/** Trigger a browser download for a single file. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

interface ConverterPageProps {
  initialDirection: ConversionDirection
  landingSource: ConverterLandingSource
}

function releaseEntries(entries: readonly FileEntry[]): void {
  for (const entry of entries) URL.revokeObjectURL(entry.originalUrl)
}

export function ConverterPage({ initialDirection, landingSource }: ConverterPageProps) {
  const t = useT()
  const pageCopy = t.converter.page
  const [direction, setDirection] = useState(initialDirection)
  const directionConfig = DIRECTION_CONFIG[direction]
  const utilityKey = directionConfig.utilityKey
  const [files, setFiles] = useState<FileEntry[]>([])
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(directionConfig.output)
  const [quality, setQuality] = useState(85)
  const [isConverting, setIsConverting] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const [rejection, setRejection] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const settingsVersionRef = useRef(0)
  const selectionVersionRef = useRef(0)
  const conversionVersionRef = useRef(0)
  const conversionControllerRef = useRef<AbortController | null>(null)
  const filesRef = useRef<FileEntry[]>([])
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      selectionVersionRef.current += 1
      conversionVersionRef.current += 1
      conversionControllerRef.current?.abort()
      conversionControllerRef.current = null
      releaseEntries(filesRef.current)
      filesRef.current = []
    }
  }, [])

  // When output format or quality changes, reset all converted/converting
  // entries so they are re-converted with the new settings.  Increment the
  // version ref so any in-flight async conversion can detect the change.
  useEffect(() => {
    settingsVersionRef.current += 1
    conversionVersionRef.current += 1
    conversionControllerRef.current?.abort()
    conversionControllerRef.current = null
    setIsConverting(false)
    setFiles((prev) =>
      {
        const next = prev.map((file) => (
          file.status === 'pending'
            ? file
            : {
                ...file,
                convertedBlob: null,
                convertedSize: null,
                status: 'pending' as const,
                error: undefined,
              }
        ))
        filesRef.current = next
        return next
      },
    )
  }, [outputFormat, quality])

  const inputLabel = directionConfig.input === 'png' ? 'PNG' : 'JPG / JPEG'

  /** Add files to the list. */
  const addFiles = useCallback(async (incoming: FileList | File[]) => {
    const selectionVersion = ++selectionVersionRef.current
    const entries: FileEntry[] = []
    let batchUsage = converterBatchUsage(filesRef.current.map((entry) => ({
      fileBytes: entry.originalSize,
      width: entry.width,
      height: entry.height,
    })))
    let rejectedReason: string | null = null
    for (const file of Array.from(incoming)) {
      if (batchUsage.files >= MAX_CONVERTER_BATCH_FILES) {
        rejectedReason = t.converter.errorsText.tooManyFiles
        break
      }
      if (batchUsage.inputBytes + file.size > MAX_CONVERTER_BATCH_INPUT_BYTES) {
        rejectedReason = t.converter.errorsText.aggregateInputTooLarge
        break
      }
      if (file.size > MAX_CONVERTER_FILE_SIZE) {
        rejectedReason = t.converter.errorsText.tooLarge
        continue
      }
      const validation = await validateConverterFile(file)
      if (!mountedRef.current || selectionVersionRef.current !== selectionVersion) {
        releaseEntries(entries)
        return
      }
      if (!validation.ok) {
        rejectedReason = validation.reason === 'too_many_pixels'
          ? t.converter.errorsText.tooManyPixels
          : validation.reason === 'unsupported'
            ? t.converter.errorsText.unsupported
            : t.converter.errorsText.invalidContainer
        continue
      }
      if (validation.format !== directionConfig.input) {
        rejectedReason = t.converter.errorsText.wrongDirection
        continue
      }
      const appendResult = appendConverterBatchUsage(batchUsage, file.size, validation.meta)
      if (!appendResult.ok) {
        rejectedReason = appendResult.reason === 'too_many_files'
          ? t.converter.errorsText.tooManyFiles
          : appendResult.reason === 'aggregate_input_too_large'
            ? t.converter.errorsText.aggregateInputTooLarge
            : appendResult.reason === 'aggregate_pixels_too_large'
              ? t.converter.errorsText.aggregatePixelsTooLarge
              : t.converter.errorsText.outputZipWorkloadTooLarge
        continue
      }
      batchUsage = appendResult.usage
      entries.push({
        id: uid(),
        file,
        originalUrl: URL.createObjectURL(file),
        originalSize: file.size,
        convertedBlob: null,
        convertedSize: null,
        status: 'pending',
        detectedFormat: validation.format,
        width: validation.meta.width,
        height: validation.meta.height,
      })
    }
    if (!mountedRef.current || selectionVersionRef.current !== selectionVersion) {
      releaseEntries(entries)
      return
    }
    setRejection(rejectedReason)
    const next = [...filesRef.current, ...entries]
    filesRef.current = next
    setFiles(next)
  }, [directionConfig.input, t.converter.errorsText])

  /** Handle file input change. */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        void addFiles(e.target.files)
        e.target.value = ''
      }
    },
    [addFiles],
  )

  /** Handle drag-and-drop events. */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDropActive(false)
      if (e.dataTransfer.files.length > 0) {
        void addFiles(e.dataTransfer.files)
      }
    },
    [addFiles],
  )

  /** Remove a single file entry and revoke its URL. */
  const removeFile = useCallback((id: string) => {
    setFiles((prev) => {
      const entry = prev.find((f) => f.id === id)
      if (entry) releaseEntries([entry])
      const next = prev.filter((f) => f.id !== id)
      filesRef.current = next
      return next
    })
  }, [])

  /** Clear all files. */
  const clearAll = useCallback(() => {
    selectionVersionRef.current += 1
    conversionVersionRef.current += 1
    conversionControllerRef.current?.abort()
    conversionControllerRef.current = null
    releaseEntries(filesRef.current)
    filesRef.current = []
    setFiles([])
    setIsConverting(false)
    nextId = 0
  }, [])

  /** Convert all pending files. */
  const handleConvert = useCallback(async () => {
    const telemetry = analyticsParams(direction, landingSource)
    toolInvoke(utilityKey, telemetry)
    conversionControllerRef.current?.abort()
    const controller = new AbortController()
    conversionControllerRef.current = controller
    const conversionVersion = ++conversionVersionRef.current
    setIsConverting(true)

    const versionAtStart = settingsVersionRef.current
    const startTime = performance.now()
    const pending = files.filter((f) => f.status === 'pending')
    const updated = [...files]

    for (const entry of pending) {
      const idx = updated.findIndex((f) => f.id === entry.id)
      if (idx === -1) continue
      if (
        controller.signal.aborted
        || conversionVersionRef.current !== conversionVersion
        || !mountedRef.current
      ) return

      updated[idx] = { ...entry, status: 'converting' }
      filesRef.current = [...updated]
      setFiles([...updated])

      try {
        const blob = await convertEntry(
          entry,
          outputFormat,
          quality,
          t.converter.errorsText,
          controller.signal,
        )
        if (
          controller.signal.aborted
          || conversionVersionRef.current !== conversionVersion
          || !mountedRef.current
        ) return
        // If settings changed while this file was being converted, put it
        // back to pending instead of done so it re-converts with new
        // settings on the next click.  The useEffect already reset it to
        // pending in state, so we mirror that here to avoid overriding.
        if (settingsVersionRef.current !== versionAtStart) {
          updated[idx] = { ...entry, status: 'pending' as const }
        } else {
          updated[idx] = {
            ...entry,
            convertedBlob: blob,
            convertedSize: blob.size,
            status: 'done',
          }
        }
        filesRef.current = [...updated]
        setFiles([...updated])
      } catch (err) {
        if (
          controller.signal.aborted
          || conversionVersionRef.current !== conversionVersion
          || !mountedRef.current
        ) return
        updated[idx] = {
          ...entry,
          status: 'error',
          error: err instanceof Error ? err.message : t.converter.errorsText.generic,
        }
        filesRef.current = [...updated]
        setFiles([...updated])
      }
    }

    if (
      controller.signal.aborted
      || conversionVersionRef.current !== conversionVersion
      || !mountedRef.current
    ) return
    const durationMs = Math.round(performance.now() - startTime)
    const allDone = updated.every((f) => f.status === 'done' || f.status === 'error')
    if (allDone) {
      const successCount = updated.filter((f) => f.status === 'done').length
      if (successCount > 0) {
        toolSuccess(utilityKey, durationMs, telemetry)
      } else {
        toolError(utilityKey, 'all_failed', telemetry)
      }
    }

    conversionControllerRef.current = null
    setIsConverting(false)
  }, [direction, files, landingSource, outputFormat, quality, t.converter.errorsText, utilityKey])

  const selectDirection = useCallback((nextDirection: ConversionDirection) => {
    if (nextDirection === direction || isConverting) return
    selectionVersionRef.current += 1
    clearAll()
    setRejection(null)
    setDirection(nextDirection)
    setOutputFormat(DIRECTION_CONFIG[nextDirection].output)
  }, [clearAll, direction, isConverting])

  /** Download a single converted file. */
  const handleDownloadOne = useCallback(
    (entry: FileEntry) => {
      if (!entry.convertedBlob) return
      const filename = outputFilename(entry.file.name, outputFormat)
      downloadBlob(entry.convertedBlob, filename)
    },
    [outputFormat],
  )

  /** Download all converted files as ZIP using JSZip. */
  const handleDownloadAll = useCallback(async () => {
    const done = files.filter((f) => f.status === 'done' && f.convertedBlob)
    if (done.length === 0) return
    if (!isConverterZipWorkloadAllowed(done.map((entry) => entry.convertedBlob!.size))) {
      setRejection(t.converter.errorsText.outputZipWorkloadTooLarge)
      return
    }

    const { default: JSZip } = await import('jszip')
    const zip = new JSZip()

    for (const entry of done) {
      const filename = outputFilename(entry.file.name, outputFormat)
      zip.file(filename, entry.convertedBlob!)
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    downloadBlob(zipBlob, `converted-images.zip`)
  }, [files, outputFormat, t.converter.errorsText.outputZipWorkloadTooLarge])

  /** Summary stats. */
  const stats = useMemo(() => {
    const pending = files.filter((f) => f.status === 'pending').length
    const done = files.filter((f) => f.status === 'done').length
    const errors = files.filter((f) => f.status === 'error').length
    const totalOriginal = files.reduce((s, f) => s + f.originalSize, 0)
    const totalConverted = files
      .filter((f) => f.convertedSize !== null)
      .reduce((s, f) => s + (f.convertedSize ?? 0), 0)
    return { pending, done, errors, totalOriginal, totalConverted }
  }, [files])

  const canConvert = files.length > 0 && !isConverting

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={pageCopy.eyebrow}
        title={pageCopy.title}
        description={pageCopy.description}
        chips={[t.common.formats, t.common.maxFileSizePerFile]}
        collapsed={files.length > 0}
      />

      <Card className="mx-auto mb-6 max-w-3xl">
        <CardContent className="grid gap-4 p-5 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
          <div className="space-y-2">
            <div className="text-sm font-medium">{t.converter.inputFormat}</div>
            <div className="grid grid-cols-2 overflow-hidden rounded-md border">
              <button
                type="button"
                aria-pressed={direction === 'pngToJpg'}
                className={cn('px-3 py-2 text-sm', direction === 'pngToJpg' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted')}
                disabled={isConverting}
                onClick={() => selectDirection('pngToJpg')}
              >
                PNG
              </button>
              <button
                type="button"
                aria-pressed={direction === 'jpgToPng'}
                className={cn('border-l px-3 py-2 text-sm', direction === 'jpgToPng' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted')}
                disabled={isConverting}
                onClick={() => selectDirection('jpgToPng')}
              >
                JPG / JPEG
              </button>
            </div>
          </div>
          <div className="pb-2 text-center text-muted-foreground" aria-hidden>→</div>
          <div className="space-y-2">
            <div className="text-sm font-medium">{t.converter.outputFormat}</div>
            <div className="grid grid-cols-2 overflow-hidden rounded-md border">
              <button
                type="button"
                aria-pressed={outputFormat === 'image/jpeg'}
                className={cn('px-3 py-2 text-sm', outputFormat === 'image/jpeg' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted')}
                disabled={isConverting}
                onClick={() => selectDirection('pngToJpg')}
              >
                JPG
              </button>
              <button
                type="button"
                aria-pressed={outputFormat === 'image/png'}
                className={cn('border-l px-3 py-2 text-sm', outputFormat === 'image/png' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted')}
                disabled={isConverting}
                onClick={() => selectDirection('jpgToPng')}
              >
                PNG
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div
        className={cn(
          'grid grid-cols-1 gap-6',
          files.length > 0 && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start',
        )}
      >
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              files.length > 0 && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-sm font-semibold text-muted-foreground">
                IMG
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{t.converter.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  files.length > 0 && 'hidden sm:block',
                )}
              >
                {t.converter.dropDescription.replace('{format}', inputLabel)}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".png,.jpg,.jpeg"
                multiple
                className="hidden"
                onChange={handleInputChange}
              />
              <div className="flex flex-wrap justify-center gap-3">
                <Button onClick={() => inputRef.current?.click()}>
                  {t.converter.chooseFiles}
                </Button>
                {files.length > 0 && (
                  <Button variant="outline" onClick={clearAll}>
                    {t.converter.clearAll}
                  </Button>
                )}
              </div>
              {rejection && (
                <p role="alert" className="mt-4 text-sm text-destructive">{rejection}</p>
              )}
            </CardContent>
          </Card>

          {files.length > 0 && (
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-normal">
                  {t.converter.filesTitle} ({files.length})
                </h2>
                <div className="flex gap-2">
                  {stats.done > 0 && (
                    <Button variant="outline" size="sm" onClick={handleDownloadAll}>
                      {t.converter.downloadAll} ({stats.done})
                    </Button>
                  )}
                  {canConvert && (
                    <Button size="sm" onClick={handleConvert}>
                      {stats.pending > 0
                        ? `${t.common.convert} (${stats.pending})`
                        : t.common.convert}
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {files.map((entry) => {
                  const format = entry.detectedFormat === 'jpeg' ? 'JPG' : 'PNG'
                  const convertedDelta =
                    entry.originalSize > 0 && entry.convertedSize !== null
                      ? Math.round(
                          ((entry.convertedSize - entry.originalSize) / entry.originalSize) *
                            100,
                        )
                      : null

                  return (
                    <Card key={entry.id} className="overflow-hidden shadow-sm">
                      <CardContent className="p-0">
                        <div className="aspect-video bg-muted">
                          <img
                            src={entry.originalUrl}
                            alt={entry.file.name}
                            className="h-full w-full object-contain"
                          />
                        </div>

                        <div className="space-y-4 p-4">
                          <div className="min-w-0">
                            <div
                              className="truncate text-sm font-medium"
                              title={entry.file.name}
                            >
                              {entry.file.name}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                                {format}
                              </span>
                              <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                {formatSize(entry.originalSize)}
                              </span>
                              {entry.convertedSize !== null && (
                                <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                  {formatSize(entry.convertedSize)}
                                  {convertedDelta !== null && (
                                    <span
                                      className={cn(
                                        'ml-1 font-medium',
                                        convertedDelta <= 0
                                          ? 'text-primary'
                                          : 'text-muted-foreground',
                                      )}
                                    >
                                      {convertedDelta > 0 ? '+' : ''}
                                      {convertedDelta}%
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          {entry.status === 'converting' && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              {t.common.converting}
                            </div>
                          )}

                          {entry.status === 'done' && entry.convertedBlob && (
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-sm font-medium text-primary">
                                {t.common.done}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadOne(entry)}
                              >
                                {t.common.download}
                              </Button>
                            </div>
                          )}

                          {entry.status === 'error' && (
                            <div className="text-sm text-destructive">
                              {entry.error ?? t.common.errorConverting}
                            </div>
                          )}

                          {entry.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              disabled={isConverting}
                              onClick={() => removeFile(entry.id)}
                            >
                              {t.common.remove}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </section>
          )}
        </div>

        {files.length > 0 && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">
                    {t.converter.settings}
                  </h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{t.converter.outputFormat}</div>
                  <div className="rounded-md border bg-muted px-3 py-2 text-center text-sm font-medium">
                    {outputFormat === 'image/jpeg' ? 'JPG' : 'PNG'}
                  </div>
                  <p className="text-xs leading-5 text-muted-foreground">
                    {outputFormat === 'image/jpeg'
                      ? t.converter.transparencyWarning
                      : t.converter.pngQualityNote}
                  </p>
                </div>

                {outputFormat === 'image/jpeg' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">{t.converter.jpegQuality}</span>
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {quality}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={10}
                      max={100}
                      step={1}
                      value={quality}
                      disabled={isConverting}
                      onChange={(e) => setQuality(Number(e.target.value))}
                      className="w-full accent-primary"
                    />
                  </div>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleConvert}
                  disabled={!canConvert || stats.pending === 0}
                >
                  {isConverting
                    ? t.common.converting
                    : stats.pending > 0
                      ? `${t.common.convert} (${stats.pending})`
                      : t.converter.noFilesQueued}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{t.converter.summary}</h2>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.pending}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.pending}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.done}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.done}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.errors}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.errors}</div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{t.converter.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(stats.totalOriginal)}
                    </strong>
                  </div>
                  {stats.done > 0 && (
                    <div className="flex justify-between gap-3">
                      <span>{t.converter.convertedSize}</span>
                      <strong className="font-medium text-foreground">
                        {formatSize(stats.totalConverted)}
                      </strong>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {t.converter.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 border-t pt-8">
        <h2 className="text-2xl font-semibold tracking-normal">
          {pageCopy.howToTitle}
        </h2>
        <ol className="mt-5 grid gap-4 md:grid-cols-2">
          {pageCopy.howTo.map((step, index) => (
            <li key={step} className="rounded-lg border bg-card p-4 text-sm leading-6">
              <span className="mr-2 font-semibold text-primary">{index + 1}.</span>
              {step}
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {pageCopy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
